import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

/**
 * i18n för Yatzy.
 *
 * Svenska är appens standardspråk; engelska väljs aktivt via språkväljaren i
 * menyn (ingen auto-detektering – det skulle överraska svenska användare med
 * engelsk webbläsare och omvänt). Saknas en nyckel i valt språk faller t()
 * tillbaka till svenska och därefter till själva nyckeln – appen kan aldrig
 * "gå sönder" av en saknad översättning, bara visa svenska.
 *
 * Täckning (2026-08-15): startskärm, multiplayer-setup, spelvyns kärna,
 * poängkortens kategorier och game over-rutan. Sekundära ytor (achievements,
 * statistik, regler/hjälp, fun facts, online-läget) är fortsatt svenska –
 * se docs/I18N.md.
 */

export type Locale = 'sv' | 'en';
interface Dict { [k: string]: string; }
interface Messages { [locale: string]: Dict; }

export const sv: Dict = {
  // App/fel
  'app.error.title': 'Ojdå – nåt gick fel',
  'app.error.reload': 'Försök ladda om sidan. Felet är loggat.',
  'recover.resume': 'Fortsätt senaste spel',
  'recover.dismiss': 'Ignorera',
  'recover.title': 'Återställning',

  // Startskärm
  'start.chooseMode': '🎲 Välj spelläge',
  'start.resume': '▶️ Fortsätt spel',
  'start.resumeSub': '{mode} – omgång {round}',
  'start.play': '🎲 Spela',
  'start.daily.heading': '📅 Dagens utmaning',
  'start.daily.button': '📅 Dagens utmaning',
  'start.daily.desc': 'Samma tärningar för alla – ny utmaning varje dag!',
  'start.single.heading': '🎯 En spelare',
  'start.single.classic': '🎲 Klassiskt',
  'start.single.desc': 'Spela själv och försök slå dina egna rekord!',
  'start.single.maxi': '🎲 Maxi Yatzy',
  'start.single.forced': '📏 Tvingande',
  'start.single.forcedMaxi': '🧭 Tvingande Maxi',
  'start.multi.heading': '👥 Flera spelare',
  'start.multi.local': '👥 Lokal Multiplayer',
  'start.multi.online': '🌐 Online Multiplayer',
  'start.multi.desc': 'Lokalt: turas om på samma enhet. Online: spela mot andra via rumskod.',
  'start.advanced.heading': '🚀 Avancerade',
  'start.advanced.button': '🚀 Avancerade lägen',
  'start.help': '📘 Hjälp / Regler',
  'start.highscore.title': '🏆 Highscore (Lokal)',
  'start.highscore.mode': 'Läge:',
  'start.highscore.empty': 'Inga highscores ännu.',
  'start.highscore.reverseNote': 'Reverse: lägst poäng är bäst.',

  // Multiplayer-setup
  'setup.title': '🎲 Multiplayer Setup',
  'setup.mode': 'Spelläge:',
  'setup.mode.classic': 'Klassiskt',
  'setup.mode.maxi': 'Maxi',
  'setup.mode.forced': 'Tvingande',
  'setup.mode.forcedMaxi': 'Tvingande Maxi',
  'setup.modeNote': 'Speciallägen som Speed, Reverse, Blitz och Best of 3 spelas i singleplayer.',
  'setup.ai': '🤖 AI-motståndare:',
  'setup.ai.none': 'Ingen',
  'setup.ai.easy': 'Lätt',
  'setup.ai.medium': 'Medel',
  'setup.ai.hard': 'Svår',
  'setup.aiNote': 'AI:n spelar sin tur automatiskt och läggs sist i turordningen. Med AI kan du spela själv (1 spelare).',
  'setup.playerCount': 'Antal spelare:',
  'setup.playerNames': 'Spelarnamn:',
  'setup.playerPlaceholder': 'Spelare {n}',
  'setup.start': '🚀 Starta spel!',
  'setup.aiRequired': 'Välj en AI-motståndare för att spela själv',
  'setup.soloVsAi': '1 (mot AI)',
  'setup.soloNote': 'Välj "1 (mot AI)" för att spela ensam mot datorn – en AI-motståndare läggs till automatiskt.',

  // Spelvyn
  'game.title.maxi': ' - Maxi',
  'game.title.daily': ' - Dagens utmaning',
  'game.subtitle': 'Kasta tärningarna och få högsta möjliga poäng!',
  'game.nextDaily': '⏳ Nästa dagliga utmaning (UTC):',
  'game.round': 'Omgång {round} av {total}',
  'game.firstRoll': 'Dags för första kastet',
  'game.lastRoll': 'Dags för sista kastet',
  'game.nthRoll': 'Dags för kast {n}',
  'game.pickCategory': 'Välj en kategori',
  'game.rollBtn.new': 'Ny omgång - Kasta tärningarna',
  'game.rollBtn.none': 'Inga kast kvar',
  'game.rollBtn.last': 'Sista kastet',
  'game.rollBtn.mid': 'Kast {n} ({left} kast kvar efter detta kast)',
  'game.rollBtn.rolling': 'Kastar...',
  'game.highscore': '🏆 Highscore',
  'game.statistics': '📊 Statistik',
  'game.newGame': '🔄 Nytt spel',
  'game.ghost': '{delta} p mot ditt rekord ({final} p)',
  'game.undo': '↩️ Ångra',
  'game.undoToast': '{category}: {score} poäng',
  'game.strikeConfirm': 'Stryka {category}? Du får 0 poäng.',
  'game.strikeYes': 'Ja, stryka',
  'game.strikeNo': 'Nej',
  'game.homeConfirm.resumable': 'Gå till startmenyn? Du kan fortsätta spelet därifrån.',
  'game.homeConfirm.lost': 'Gå till startmenyn? Pågående spel förloras.',
  'game.homeYes': 'Ja, till menyn',
  'game.homeNo': 'Nej',
  'game.newGameConfirm': 'Avbryta nuvarande spel och starta nytt?',
  'game.newGameYes': 'Ja, starta nytt',
  'game.newGameNo': 'Nej, fortsätt',
  'game.reminderPrompt': 'Vill du bli påmind om dagens utmaning? 🎲',
  'game.reminderYes': 'Ja, påminn mig',
  'game.reminderNo': 'Nej tack',
  'game.reminderOn': '🔔 Påminnelser aktiverade!',

  // Poängkort
  'score.upper': 'Övre sektion',
  'score.lower': 'Nedre sektion',
  'score.upperSum': 'Summa övre sektion:',
  'score.bonusRow': 'Bonus (63+ poäng):',
  'score.bonusRowMaxi': 'Bonus (84+ poäng):',
  'score.total': 'Total: {total} poäng',
  'score.bonusLeft': '{points} poäng kvar till bonus',
  'score.forcedNext': 'Tvingande läge: nästa kategori som måste fyllas är',
  'score.reverseNote': 'Lägre poäng är bättre!',

  // Kategorier (namn + beskrivning)
  'cat.ones.name': 'Ettor', 'cat.ones.desc': 'Summa av alla ettor',
  'cat.twos.name': 'Tvåor', 'cat.twos.desc': 'Summa av alla tvåor',
  'cat.threes.name': 'Treor', 'cat.threes.desc': 'Summa av alla treor',
  'cat.fours.name': 'Fyror', 'cat.fours.desc': 'Summa av alla fyror',
  'cat.fives.name': 'Femmor', 'cat.fives.desc': 'Summa av alla femmor',
  'cat.sixes.name': 'Sexor', 'cat.sixes.desc': 'Summa av alla sexor',
  'cat.onePair.name': 'Ett par', 'cat.onePair.desc': 'Två tärningar med samma värde',
  'cat.twoPairs.name': 'Två par', 'cat.twoPairs.desc': 'Två olika par',
  'cat.threeOfAKind.name': 'Triss', 'cat.threeOfAKind.desc': 'Tre tärningar med samma värde',
  'cat.fourOfAKind.name': 'Fyrtal', 'cat.fourOfAKind.desc': 'Fyra tärningar med samma värde',
  'cat.fiveOfAKind.name': 'Femtal', 'cat.fiveOfAKind.desc': 'Fem lika',
  'cat.smallStraight.name': 'Liten stege', 'cat.smallStraight.desc': '1-2-3-4-5 (15 poäng)',
  'cat.largeStraight.name': 'Stor stege', 'cat.largeStraight.desc': '2-3-4-5-6 (20 poäng)',
  'cat.fullStraight.name': 'Full stege', 'cat.fullStraight.desc': '1-2-3-4-5-6 (21 poäng)',
  'cat.fullHouse.name': 'Kåk', 'cat.fullHouse.desc': 'Tre lika + två lika',
  'cat.hus.name': 'Hus', 'cat.hus.desc': 'Två triss (3 + 3)',
  'cat.torn.name': 'Torn', 'cat.torn.desc': 'Fyra + par (4 + 2)',
  'cat.chance.name': 'Chans', 'cat.chance.desc': 'Summan av alla tärningar',
  'cat.yatzy.name': 'Yatzy', 'cat.yatzy.desc': 'Alla lika',

  // Game over
  'over.winner': '🎉 {name} vinner!',
  'over.done': '🎉 Spelet slut!',
  'over.tab.result': 'Resultat & spara',
  'over.tab.highlights': 'Höjdpunkter',
  'over.tab.highscores': 'Topplista',
  'over.points': 'poäng',
  'over.newGame': '🔄 Nytt spel',
  'over.fullList': '🏆 Öppna full topplista',
  'over.share': '📤 Dela',
  'over.shareCopied': '✅ Kopierat!',
  'over.namePlaceholder': 'Ange ditt namn för topplistan',
  'over.save': 'Spara resultat',
  'over.saving': 'Sparar...',
  'over.saved': '✅ Ditt resultat är sparat!',

  // Meny & 3D-spelbord
  'menu.language': '🌐 Språk',
  'board.title': '3D Raytracat Spelbord',
  'board.chooseTable': 'Välj spelbord',
  'board.rollingArena': '🎯 Kastyta',
  'board.clickToKeep': 'Klicka på en tärning för att spara / släppa',
  'board.readyToRoll': 'Redo för kast',
  'board.keepTray': 'Spara-hylla ({locked}/{total})',
  'board.keepAll': 'Spara alla',
  'board.releaseAll': 'Släpp alla',
};

export const en: Dict = {
  'app.error.title': 'Oops – something went wrong',
  'app.error.reload': 'Try reloading the page. The error was logged.',
  'recover.resume': 'Resume last game',
  'recover.dismiss': 'Dismiss',
  'recover.title': 'Recovery',

  'start.chooseMode': '🎲 Choose game mode',
  'start.resume': '▶️ Resume game',
  'start.resumeSub': '{mode} – round {round}',
  'start.play': '🎲 Play',
  'start.daily.heading': '📅 Daily challenge',
  'start.daily.button': '📅 Daily challenge',
  'start.daily.desc': 'Same dice for everyone – a new challenge every day!',
  'start.single.heading': '🎯 Single player',
  'start.single.classic': '🎲 Classic',
  'start.single.desc': 'Play solo and try to beat your own records!',
  'start.single.maxi': '🎲 Maxi Yatzy',
  'start.single.forced': '📏 Forced order',
  'start.single.forcedMaxi': '🧭 Forced Maxi',
  'start.multi.heading': '👥 Multiplayer',
  'start.multi.local': '👥 Local multiplayer',
  'start.multi.online': '🌐 Online multiplayer',
  'start.multi.desc': 'Local: take turns on one device. Online: play others via room code.',
  'start.advanced.heading': '🚀 Advanced',
  'start.advanced.button': '🚀 Advanced modes',
  'start.help': '📘 Help / Rules',
  'start.highscore.title': '🏆 High scores (local)',
  'start.highscore.mode': 'Mode:',
  'start.highscore.empty': 'No high scores yet.',
  'start.highscore.reverseNote': 'Reverse: lowest score wins.',

  'setup.title': '🎲 Multiplayer setup',
  'setup.mode': 'Game mode:',
  'setup.mode.classic': 'Classic',
  'setup.mode.maxi': 'Maxi',
  'setup.mode.forced': 'Forced order',
  'setup.mode.forcedMaxi': 'Forced Maxi',
  'setup.modeNote': 'Special modes like Speed, Reverse, Blitz and Best of 3 are single-player.',
  'setup.ai': '🤖 AI opponent:',
  'setup.ai.none': 'None',
  'setup.ai.easy': 'Easy',
  'setup.ai.medium': 'Medium',
  'setup.ai.hard': 'Hard',
  'setup.aiNote': 'The AI plays its turn automatically and goes last. With an AI you can play solo (1 player).',
  'setup.playerCount': 'Number of players:',
  'setup.playerNames': 'Player names:',
  'setup.playerPlaceholder': 'Player {n}',
  'setup.start': '🚀 Start game!',
  'setup.aiRequired': 'Pick an AI opponent to play solo',
  'setup.soloVsAi': '1 (vs AI)',
  'setup.soloNote': 'Pick "1 (vs AI)" to play solo against the computer – an AI opponent is added automatically.',

  'game.title.maxi': ' - Maxi',
  'game.title.daily': ' - Daily challenge',
  'game.subtitle': 'Roll the dice and score as high as you can!',
  'game.nextDaily': '⏳ Next daily challenge (UTC):',
  'game.round': 'Round {round} of {total}',
  'game.firstRoll': 'Time for the first roll',
  'game.lastRoll': 'Time for the last roll',
  'game.nthRoll': 'Time for roll {n}',
  'game.pickCategory': 'Pick a category',
  'game.rollBtn.new': 'New round - Roll the dice',
  'game.rollBtn.none': 'No rolls left',
  'game.rollBtn.last': 'Last roll',
  'game.rollBtn.mid': 'Roll {n} ({left} left after this roll)',
  'game.rollBtn.rolling': 'Rolling...',
  'game.highscore': '🏆 High scores',
  'game.statistics': '📊 Statistics',
  'game.newGame': '🔄 New game',
  'game.ghost': '{delta} pts vs your record ({final} pts)',
  'game.undo': '↩️ Undo',
  'game.undoToast': '{category}: {score} points',
  'game.strikeConfirm': 'Strike {category}? You get 0 points.',
  'game.strikeYes': 'Yes, strike it',
  'game.strikeNo': 'No',
  'game.homeConfirm.resumable': 'Go to the start menu? You can resume the game from there.',
  'game.homeConfirm.lost': 'Go to the start menu? The current game will be lost.',
  'game.homeYes': 'Yes, go to menu',
  'game.homeNo': 'No',
  'game.newGameConfirm': 'Abandon the current game and start over?',
  'game.newGameYes': 'Yes, start over',
  'game.newGameNo': 'No, keep playing',
  'game.reminderPrompt': 'Want a reminder about the daily challenge? 🎲',
  'game.reminderYes': 'Yes, remind me',
  'game.reminderNo': 'No thanks',
  'game.reminderOn': '🔔 Reminders enabled!',

  'score.upper': 'Upper section',
  'score.lower': 'Lower section',
  'score.upperSum': 'Upper section sum:',
  'score.bonusRow': 'Bonus (63+ points):',
  'score.bonusRowMaxi': 'Bonus (84+ points):',
  'score.total': 'Total: {total} points',
  'score.bonusLeft': '{points} points left for the bonus',
  'score.forcedNext': 'Forced order: next category to fill is',
  'score.reverseNote': 'Lower score is better!',

  'cat.ones.name': 'Ones', 'cat.ones.desc': 'Sum of all ones',
  'cat.twos.name': 'Twos', 'cat.twos.desc': 'Sum of all twos',
  'cat.threes.name': 'Threes', 'cat.threes.desc': 'Sum of all threes',
  'cat.fours.name': 'Fours', 'cat.fours.desc': 'Sum of all fours',
  'cat.fives.name': 'Fives', 'cat.fives.desc': 'Sum of all fives',
  'cat.sixes.name': 'Sixes', 'cat.sixes.desc': 'Sum of all sixes',
  'cat.onePair.name': 'One pair', 'cat.onePair.desc': 'Two dice of the same value',
  'cat.twoPairs.name': 'Two pairs', 'cat.twoPairs.desc': 'Two different pairs',
  'cat.threeOfAKind.name': 'Three of a kind', 'cat.threeOfAKind.desc': 'Three dice of the same value',
  'cat.fourOfAKind.name': 'Four of a kind', 'cat.fourOfAKind.desc': 'Four dice of the same value',
  'cat.fiveOfAKind.name': 'Five of a kind', 'cat.fiveOfAKind.desc': 'Five of a kind',
  'cat.smallStraight.name': 'Small straight', 'cat.smallStraight.desc': '1-2-3-4-5 (15 points)',
  'cat.largeStraight.name': 'Large straight', 'cat.largeStraight.desc': '2-3-4-5-6 (20 points)',
  'cat.fullStraight.name': 'Full straight', 'cat.fullStraight.desc': '1-2-3-4-5-6 (21 points)',
  'cat.fullHouse.name': 'Full house', 'cat.fullHouse.desc': 'Three of a kind + a pair',
  'cat.hus.name': 'House', 'cat.hus.desc': 'Two triples (3 + 3)',
  'cat.torn.name': 'Tower', 'cat.torn.desc': 'Four + a pair (4 + 2)',
  'cat.chance.name': 'Chance', 'cat.chance.desc': 'Sum of all dice',
  'cat.yatzy.name': 'Yatzy', 'cat.yatzy.desc': 'All the same',

  'over.winner': '🎉 {name} wins!',
  'over.done': '🎉 Game over!',
  'over.tab.result': 'Result & save',
  'over.tab.highlights': 'Highlights',
  'over.tab.highscores': 'Leaderboard',
  'over.points': 'points',
  'over.newGame': '🔄 New game',
  'over.fullList': '🏆 Open full leaderboard',
  'over.share': '📤 Share',
  'over.shareCopied': '✅ Copied!',
  'over.namePlaceholder': 'Enter your name for the leaderboard',
  'over.save': 'Save result',
  'over.saving': 'Saving...',
  'over.saved': '✅ Your result was saved!',

  'menu.language': '🌐 Language',
  'board.title': '3D Raytraced Table',
  'board.chooseTable': 'Choose game table',
  'board.rollingArena': '🎯 Rolling Arena',
  'board.clickToKeep': 'Click a die to keep / release',
  'board.readyToRoll': 'Ready to roll',
  'board.keepTray': 'Keep Tray ({locked}/{total})',
  'board.keepAll': 'Keep all',
  'board.releaseAll': 'Release all',
};

const messages: Messages = { sv, en };

/** Ersätter {platshållare} i en översättningssträng. */
export function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
}

interface I18nCtx {
  t: (k: string, vars?: Record<string, string | number>) => string;
  locale: Locale;
  setLocale: (l: Locale) => void;
}
const I18nContext = createContext<I18nCtx | null>(null);

/** Svenska är default – engelska väljs aktivt via språkväljaren (ingen auto-detektering). */
function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem('yatzy-locale');
    if (saved === 'en' || saved === 'sv') return saved;
  } catch {}
  return 'sv';
}

export const I18nProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [locale, setLocale] = useState<Locale>(initialLocale);
  useEffect(() => { try { localStorage.setItem('yatzy-locale', locale); } catch {} }, [locale]);
  useEffect(() => { try { document.documentElement.lang = locale; } catch {} }, [locale]);
  // Stabil identitet (per locale) så att t kan användas i hook-beroenden.
  const t = useCallback(
    (k: string, vars?: Record<string, string | number>) =>
      interpolate((messages[locale] && messages[locale][k]) || messages.sv[k] || k, vars),
    [locale]
  );
  const value = useMemo(() => ({ t, locale, setLocale }), [t, locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nCtx {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    return { t: (k: string, vars?: Record<string, string | number>) => interpolate(sv[k] || k, vars), locale: 'sv', setLocale: () => {} };
  }
  return ctx;
}

export function useT() {
  return useI18n().t;
}
